import { type ReactNode, useEffect, useMemo } from 'react'
import { DevTools, FormatSimple, Tolgee, TolgeeProvider } from '@tolgee/react'
import type { PublicSiteConfig } from '@kcm/contracts'
import { catalogs } from '@kcm/i18n'
import { detectCountry } from '../lib/geo'
import {
  fontClassFor,
  hasExplicitLocale,
  localeForCountry,
  resolveInitialLocale,
  writeLangCookie,
} from '../lib/i18n'

const FONT_CLASSES = ['font-thai', 'font-jp', 'font-tc', 'font-kr']

/**
 * Wires Tolgee for the funnel (PLAN.md §17).
 *
 * The bundled catalogs are the source of truth, so the funnel renders in every
 * language even with no network — onboarding must never be blocked by a
 * translation CDN. When the DB carries a Tolgee client key (set in the admin,
 * never in the bundle) the SDK also pulls over-the-air updates on top.
 */
export function I18nProvider({ site, children }: { site: PublicSiteConfig; children: ReactNode }) {
  const tolgee = useMemo(() => {
    const chain = Tolgee().use(FormatSimple())
    if (import.meta.env.DEV) chain.use(DevTools())
    return chain.init({
      language: resolveInitialLocale(site.enabledLocales, site.defaultLocale),
      fallbackLanguage: 'en',
      availableLanguages: [...site.enabledLocales],
      staticData: catalogs,
      ...(site.tolgee ? { apiUrl: site.tolgee.apiUrl, apiKey: site.tolgee.apiKey } : {}),
    })
    // The site config is fixed for the app's lifetime, so the instance is built once.
  }, [site])

  useEffect(() => {
    const apply = (lang: string) => {
      document.documentElement.lang = lang
      document.documentElement.classList.remove(...FONT_CLASSES)
      const cls = fontClassFor(lang)
      if (cls) document.documentElement.classList.add(cls)
    }
    apply(tolgee.getLanguage() ?? site.defaultLocale)
    const sub = tolgee.on('language', (e) => {
      apply(e.value)
      writeLangCookie(e.value)
    })
    return () => sub.unsubscribe()
  }, [tolgee, site.defaultLocale])

  // Location-based default: when the visitor has not chosen a language (no
  // ?lang, no saved cookie), switch to the one for the country their IP places
  // them in. It runs after first paint because the lookup is async, and it
  // never overrides an explicit choice or an unavailable locale.
  useEffect(() => {
    if (hasExplicitLocale()) return
    let cancelled = false
    void detectCountry().then((country) => {
      if (cancelled || !country) return
      const geo = localeForCountry(country)
      if (geo && site.enabledLocales.includes(geo) && geo !== tolgee.getLanguage()) {
        void tolgee.changeLanguage(geo)
      }
    })
    return () => {
      cancelled = true
    }
  }, [tolgee, site.enabledLocales])

  return (
    <TolgeeProvider tolgee={tolgee} fallback={null}>
      {children}
    </TolgeeProvider>
  )
}
